import React from 'react';
import { View, StyleSheet, StyleProp, ViewStyle } from 'react-native';
import { IconButton } from 'react-native-paper';
import StylusTextInput from './StylusTextInput';

interface PlusMinusInputProps {
  label: string;
  value: number;
  onValueChange: (value: number) => void;
  min?: number;
  max?: number;
  required?: boolean;
  disabled?: boolean;
  style?: StyleProp<ViewStyle>;
  autoFocusOnMount?: boolean;
}

const PlusMinusInput: React.FC<PlusMinusInputProps> = ({
  label,
  value,
  onValueChange,
  min = 0,
  max,
  required = false,
  disabled = false,
  style,
  autoFocusOnMount = false,
}) => {
  const handleTextChange = (text: string) => {
    // Parse as integer, default to min (or 0) if invalid
    const parsed = parseInt(text, 10);
    if (isNaN(parsed) || text === '') {
      onValueChange(min);
    } else {
      // Clamp to min/max bounds if they exist
      let clampedValue = parsed;
      if (min !== undefined && clampedValue < min) {
        clampedValue = min;
      }
      if (max !== undefined && clampedValue > max) {
        clampedValue = max;
      }
      onValueChange(clampedValue);
    }
  };

  const handleIncrement = () => {
    if (disabled) return;
    const newValue = value + 1;
    if (max === undefined || newValue <= max) {
      onValueChange(newValue);
    }
  };

  const handleDecrement = () => {
    if (disabled) return;
    const newValue = value - 1;
    if (newValue >= min) {
      onValueChange(newValue);
    }
  };

  const isAtMin = value <= min;
  const isAtMax = max !== undefined && value >= max;

  return (
    <View style={[styles.container, style]}>
      <View style={styles.inputContainer}>
        <StylusTextInput
          label={label}
          value={value.toString()}
          onChangeText={handleTextChange}
          keyboardType="numeric"
          disabled={disabled}
          autoFocusOnMount={autoFocusOnMount}
          style={styles.textInput}
        />
      </View>
      <View style={styles.buttonContainer}>
        <IconButton
          icon="minus"
          size={24}
          onPress={handleDecrement}
          disabled={disabled || isAtMin}
          style={styles.button}
        />
        <IconButton
          icon="plus"
          size={24}
          onPress={handleIncrement}
          disabled={disabled || isAtMax}
          style={styles.button}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  inputContainer: {
    flex: 1,
  },
  textInput: {
    flex: 1,
  },
  buttonContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  button: {
    margin: 0,
  },
});

export default PlusMinusInput;
